/**
 * Typed protocol shared by the iframe-side client and the host-side bridge.
 *
 * Add a new host action in three steps:
 * 1. Add a variant to `BridgeRequest` and a matching variant to `BridgeResponse`.
 * 2. Register a handler with `createHostBridge({ handlers })` in `Shell`.
 * 3. Call it from a component with `useHostAction('<KIND>')`.
 *
 * Nothing outside `sdk/bridge/` or `components/host-bridge/` should call
 * `postMessage` directly for these messages.
 */

export const BridgeEnvelopeType = 'ANAGRAM_BRIDGE' as const
export const BridgeEnvelopeVersion = 1 as const

export type AddToCartRequest = {
  kind: 'ADD_TO_CART'
  variantId: string
  quantity: number
}

export type AddToCartResponse = {
  kind: 'ADD_TO_CART'
  success: boolean
  currencyCode?: string
  cartLineCostAmount?: number
  errorType?: 'host_unavailable' | 'timeout' | 'rejected' | 'invalid_response' | 'out_of_stock'
}

export type BridgeRequest = AddToCartRequest
export type BridgeResponse = AddToCartResponse
export type BridgeRequestKind = BridgeRequest['kind']

export type BridgeRequestOf<K extends BridgeRequestKind> = Extract<BridgeRequest, {kind: K}>
export type BridgeResponseOf<K extends BridgeRequestKind> = Extract<BridgeResponse, {kind: K}>
export type BridgeResponseBodyOf<K extends BridgeRequestKind> = Omit<BridgeResponseOf<K>, 'kind'>

export type BridgeEnvelope<TPayload> = {
  v: typeof BridgeEnvelopeVersion
  type: typeof BridgeEnvelopeType
  direction: 'request' | 'response'
  requestId: string
  payload: TPayload
}

export type BridgeRequestEnvelope = BridgeEnvelope<BridgeRequest> & {direction: 'request'}
export type BridgeResponseEnvelope = BridgeEnvelope<BridgeResponse> & {direction: 'response'}

export function isBridgeEnvelope(value: unknown): value is BridgeEnvelope<unknown> {
  if (!value || typeof value !== 'object') return false
  const envelope = value as Partial<BridgeEnvelope<unknown>>
  return (
    envelope.type === BridgeEnvelopeType &&
    envelope.v === BridgeEnvelopeVersion &&
    (envelope.direction === 'request' || envelope.direction === 'response') &&
    typeof envelope.requestId === 'string'
  )
}

export function isBridgeRequestEnvelope(value: unknown): value is BridgeRequestEnvelope {
  if (!isBridgeEnvelope(value) || value.direction !== 'request') return false
  const payload = value.payload as Partial<BridgeRequest> | undefined
  return typeof payload?.kind === 'string'
}

export function isBridgeResponseEnvelope(value: unknown): value is BridgeResponseEnvelope {
  if (!isBridgeEnvelope(value) || value.direction !== 'response') return false
  const payload = value.payload as Partial<BridgeResponse> | undefined
  return typeof payload?.kind === 'string'
}

export function createRequestId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID()
  }
  return `req_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
}
